import { COMPANY } from '@/content/company';
import { captchaConfigured, RECAPTCHA } from '@/content/recaptcha';
import { getCaptchaToken } from './recaptcha';

/**
 * The telephone number, fetched rather than shipped.
 *
 * The number is deliberately absent from this site's source - not in the markup,
 * not in this bundle, not in the JSON-LD - and is held by the backend instead.
 * `reveal-contact` hands it over per request, after verifying a reCAPTCHA v3
 * token server-side. A harvester that reads HTML and JavaScript, which is what
 * almost all of them do, finds nothing to take; getting the number requires
 * executing the page and passing the check.
 *
 * Not a secret, and not treated as one. It is on invoices and in the commercial
 * register, and any person who wants it can have it. What this removes is
 * obtaining it *for free, at scale*.
 *
 * Same-origin for the same reason the form is - see src/lib/enquiry.ts. Netlify
 * rewrites this path server-side, so `connect-src 'self'` is untouched.
 */

const ENDPOINT = '/api/forms/reveal';

/** Identifies this website to the shared backend. */
const SITE_KEY = 'skabro';

/**
 * Shorter than the form's 15s. This is one click expecting one short answer, and
 * a visitor stares at a spinner far less patiently than at a sent message.
 */
const TIMEOUT_MS = 10_000;

export interface Phone {
  /**
   * Display form, spaced as it is written on paper.
   *
   * Deliberately not illustrated with the real number here. The build emits
   * sourcemaps and publishes them, so a comment in this file is as public as the
   * markup - which would put the number back in the page source by the one route
   * nobody thinks to check.
   */
  display: string;
  /** E.164, for the `tel:` href. */
  href: string;
}

export type RevealResult = { ok: true; phone: Phone } | { ok: false; error: string };

const FALLBACK = `We could not fetch the number just now. Please email us at ${COMPANY.email} instead.`;

/**
 * Held for the lifetime of the page, so a visitor who navigates away and back
 * does not verify twice.
 *
 * Module scope rather than component state: React unmounts the component on a
 * route change and would otherwise forget. Deliberately *not* sessionStorage or a
 * cookie - the Cookie Policy's position is that nothing on this site is stored on
 * your device by us, and that claim is worth more than surviving a page reload.
 *
 * Worth keeping straight, because the two are easy to conflate: reCAPTCHA does
 * write a `_grecaptcha` entry into local storage on this route, which /cookies § 3
 * itemises. That is Google's, written by Google's code. Putting the number beside
 * it would make the entry partly ours, and the sentence above would stop being
 * true - which is the whole reason this is a module variable.
 */
let cached: Phone | null = null;

/** The number if it has already been revealed on this page, otherwise null. */
export function revealedPhone(): Phone | null {
  return cached;
}

export async function revealPhone(): Promise<RevealResult> {
  if (cached) return { ok: true, phone: cached };

  /*
   * Minted per click. Tokens are single-use and expire after two minutes, so one
   * taken at page load would be stale for anyone who reads before clicking.
   *
   * A null token is not a dead end here either: it goes to the backend as
   * `captcha_unavailable` and the backend decides. For this site that decision is
   * a refusal - the number is the thing being protected - but the refusal comes
   * with the server's own wording, and it is the server's call to make.
   */
  const captchaToken = await getCaptchaToken(RECAPTCHA.revealAction);

  try {
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        site: SITE_KEY,
        ...(captchaToken
          ? { captcha_token: captchaToken }
          : captchaConfigured()
            ? { captcha_unavailable: true }
            : {}),
      }),
      signal: AbortSignal.timeout(TIMEOUT_MS),
    });

    const body: unknown = await response.json().catch(() => null);
    const read = (key: string): string | null => {
      if (body === null || typeof body !== 'object') return null;
      const value = (body as Record<string, unknown>)[key];
      return typeof value === 'string' && value.trim() !== '' ? value : null;
    };

    if (!response.ok) {
      // The backend answers every rejection with human-readable wording, so
      // prefer it over anything invented here.
      return { ok: false, error: read('error') ?? FALLBACK };
    }

    const display = read('phone');
    const href = read('phone_href');
    if (!display || !href) {
      // A 200 that carries no number. Nothing the visitor can act on, so it gets
      // the same wording as a failure rather than rendering an empty link.
      return { ok: false, error: FALLBACK };
    }

    cached = { display, href };
    return { ok: true, phone: cached };
  } catch {
    // Offline, DNS failure, or the timeout. None of it is actionable for the
    // visitor and the useful next step is the same in every case.
    return {
      ok: false,
      error: `We could not reach the server. Check your connection and try again, or email us at ${COMPANY.email}.`,
    };
  }
}
